import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportMultiSeriesChart,
  ReportHorizontalBarChart
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {findReportBySlug} from './reportCatalogue';

/**
 * REV3 — Tuition Revenue.
 *
 * BILLING ONLY. FMS generates invoices; it does not track payment against them
 * (13 of 927 invoices carry any amount at all). So there is no collected series,
 * no outstanding, and no collection rate — see the service for the measurements.
 *
 * The charts answer the questions that billing data can actually answer:
 *
 *   Value + volume   is billing growing because we raise MORE invoices or
 *                    BIGGER ones? Bars are value, the line is invoice count on
 *                    its own axis — the two move independently and the shape of
 *                    the gap between them is the whole point.
 *   By subject       where the billing comes from.
 *   Composition      net vs VAT, with credits shown as the deduction they are.
 */

const SLUG = 'tuition-revenue';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const money = (v: any) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(Number(v) || 0);

const TuitionRevenue = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Tuition Revenue');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byPeriod = collections?.series?.by_period || [];
  const bySubject = collections?.series?.by_subject || [];
  const composition = collections?.series?.by_composition || [];
  const detail = collections?.detail;

  const columns = [
    {name: 'Invoice', selector: (row: any) => row.invoice_number || `#${row.id}`, width: '150px', wrap: true},
    {name: 'Student', selector: (row: any) => row.student || '---', wrap: true},
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Invoice date', selector: (row: any) => row.invoice_date || '---', width: '120px'},
    {name: 'Due', selector: (row: any) => row.due_date || '---', width: '110px'},
    {name: 'Net', width: '110px', right: true, selector: (row: any) => money(row.net)},
    {
      name: 'VAT',
      width: '100px',
      right: true,
      cell: (row: any) => (
        <span className={Number(row.vat) > 0 ? '' : 'text-muted'}>{money(row.vat)}</span>
      )
    },
    {
      name: 'Invoiced',
      width: '120px',
      right: true,
      cell: (row: any) => (
        <span className="font-weight-bold">{money(row.invoiced)}</span>
      )
    },
    // Sent only under a subject filter: that subject's share of the invoice,
    // which is what the headline sums. Invoiced is still the whole invoice.
    ...((detail?.rows || []).some((row: any) => row.subject_value !== undefined)
      ? [{
          name: 'Subject value',
          width: '130px',
          right: true,
          selector: (row: any) => money(row.subject_value)
        }]
      : [])
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Tuition Revenue'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Tuition Revenue</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, subject: true, student: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-lg-8">
              <ChartCard
                title="Invoiced value and invoice volume"
                subtitle="Bars are what was billed; the line is how many invoices carried it. Rising bars on a flat line means bigger invoices, not more of them."
                loading={loading}
                isEmpty={!byPeriod.length}
                height={380}>
                <ReportMultiSeriesChart
                  data={byPeriod}
                  xKey="bucket"
                  height={340}
                  series={[
                    {key: 'invoiced', name: 'Invoiced', colour: '#4A6FA5', type: 'bar'},
                    {
                      key: 'invoices',
                      name: 'Invoices',
                      colour: '#C9873A',
                      type: 'line',
                      axis: 'right'
                    }
                  ]}
                />
              </ChartCard>
            </div>
            <div className="col-lg-4">
              <ChartCard
                title="How the total is made up"
                subtitle="Net, VAT, and any credit applied against it"
                loading={loading}
                isEmpty={!composition.length}
                height={380}>
                <ReportHorizontalBarChart
                  data={composition}
                  labelKey="component"
                  valueKey="value"
                  height={200}
                  labelWidth={120}
                  colourField="component"
                  colourMap={{
                    Net: '#4A6FA5',
                    VAT: '#5B8C7E',
                    'Credit applied': '#B04A5A'
                  }}
                />
              </ChartCard>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <ChartCard
                title="Billing by subject"
                subtitle="Where the invoiced value comes from"
                loading={loading}
                isEmpty={!bySubject.length}
                height={340}>
                <ReportHorizontalBarChart
                  data={bySubject}
                  labelKey="subject"
                  valueKey="value"
                  height={280}
                  labelWidth={150}
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Invoices</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">No invoices match these filters.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TuitionRevenue;
